import {
  observable,
  action,
  computed,
  runInAction,
  makeObservable,
} from "mobx";
import getCookie from "ati-utils/getCookie";

import sortArrayOfObjectsByDateField from "../../utils/sortArrayOfObjectsByDateField";
import NotificationsApi from "../../api/NotificationsApi";
import FeedNotification from "./feedNotification";

import setCookie from "../../utils/setCookie";
import callWindowFunction from "../../utils/callWindowFunction";

class FeedsStore {
  constructor(rootStore, notificationUrl) {
    makeObservable(this, {
      unshownImportantCounter: observable,
      notificationList: observable,
      showChatMessagesText: observable,
      notifications: computed,
      haveMessages: computed,
      listenNtf: action,
      setUnshownImportantCounter: action,
      gotChatMessage: action,
      stopAllExpires: action,
      startAllExpires: action,
      initNotificationList: action,
      closeAllChannelMessages: action,
      setShowChatMessagesText: action,
      clearNotificationList: action,
      closeNotification: action,
    });

    this.rootStore = rootStore;
    this.NotificationsApi = new NotificationsApi(notificationUrl);
  }

  /** Есть ли непрочитанные сообщения */
  haveUnread = true;

  /** Максимально получаемые новые сообщения с бэка */
  unreadRequestSize = 10;

  /** Число важных сообщений для отрисовки (не более 2-х) */
  importantCount = 0;

  /** Число обычных сообщений для отрисовки (не более 5-ти) */
  usualCount = 0;

  /** Список таймаутов для показанных обычных нотификаций, чтобы по истечению времени их скрыть */
  timeouts = new Map();

  /** Кол-во непоказанных важных уведомлений */
  unshownImportantCounter = 0;

  /** Общий список нотификаций */
  notificationList = new Map();

  /** Произошла ли загрузка важных сообщений. Если да, то она не должна повториться в рамках сессии */
  notificationsWasLoaded = false;

  /** Показывать ли текст в нотификациях чата. Если false, то показывается счетчик полученных сообщений */
  showChatMessagesText = true;

  /** Отфильтрованный список нотификаций */
  get notifications() {
    const result = [];

    this.importantCount = 0;
    this.usualCount = 0;

    this.notificationList.forEach((notification) => {
      if (!this.showChatMessagesText && notification.isChat) {
        if (notification.wasShownAsGroupedMessageMember) {
          return;
        }

        const resultMessage = result.find((message) =>
          message.compareChatMessageSender(notification)
        );

        if (resultMessage && !resultMessage.wasShownAsGroupedMessageMember) {
          resultMessage.addGroupedChatMessage(notification.id);

          const timeout = this.timeouts.get(resultMessage.id);

          if (timeout) {
            clearTimeout(timeout);
            this.timeouts.delete(resultMessage.id);
          }

          this.startExpire(resultMessage);
          this.startExpire(notification);

          notification.wasShownAsGroupedMessageMember = true;

          return;
        }
      }

      if (notification.isImportant && this.importantCount < 2) {
        this.importantCount++;
        this.startExpire(notification);
        result.push(notification);
      }

      if (!notification.isImportant && this.usualCount < 5) {
        this.usualCount++;
        this.startExpire(notification);
        result.push(notification);
      }
    });

    return result;
  }

  /** Есть ли сообщения в списке */
  get haveMessages() {
    return !!this.notificationList.size;
  }

  /** Иницилизировать значение чекбокса showChatMessagesText из настроек фида  */
  initFeedSettings = () => {
    const chatMessagesTextCookieValue = getCookie(
      `feed_show_chat_messages_text_${this.rootStore.profile.userId}`
    );

    if (
      chatMessagesTextCookieValue === "0" ||
      chatMessagesTextCookieValue === "1"
    ) {
      this.setShowChatMessagesText(chatMessagesTextCookieValue === "1", false);
    }
  };

  // TODO: если понадобится добавить другие чекбоксы, то сделать метод универсальным, а не писать еще один
  /** Установить кукис для поля feed_show_chat_messages_text */
  setChatMessagesTextCookie = (value) => {
    setCookie(
      `feed_show_chat_messages_text_${this.rootStore.profile.userId}`,
      value,
      {
        domain: window.location.hostname.replace(/.*(.ati\.s.*)/g, "$1"),
        expires: 1000,
      }
    );
  };

  /** Изменить значение showChatMessagesText */
  setShowChatMessagesText = (value, shouldSetCookie = true) => {
    this.showChatMessagesText = value;

    callWindowFunction("publishToEventBus", [
      {
        subject: "feedShowChatMessagesText",
        destination: "exceptThisTab",
        body: { value: value ? "1" : "0" },
      },
    ]);

    if (shouldSetCookie) {
      this.setChatMessagesTextCookie(value ? "1" : "0");
    }
  };

  updateImportantCounter = (newValue) => {
    this.importantCount = newValue;
  };

  initNotificationList = (importantCounter) => {
    this.importantCount = importantCounter;

    if (importantCounter > 0 && !this.notificationsWasLoaded) {
      this.getUnreadImportantNtfs();

      this.notificationsWasLoaded = true;
    }
  };

  /** Метод получения новых нотификашек из доминатрикса */
  listenNtf = (ntf) => {
    try {
      const channelName = ntf?.channel?.replace(/#.+/, "");

      if (!ntf.entity_name && channelName) {
        this.gotChatMessage(ntf, channelName);
        return;
      }

      if (ntf.entity_name === "webPush") {
        this.gotNotification(ntf);
        return;
      }

      if (ntf.entity_name === "notification") {
        if (!ntf.entity) throw new TypeError("'entity' of undefined");
        this.notificationList.delete(ntf.entity.id);
        return;
      }

      if (ntf.entity_name === "totalUnreadCounter") {
        if (!ntf.entity) throw new TypeError("'entity' of undefined");
        this.setUnshownImportantCounter(ntf.entity.important);
        return;
      }
    } catch (e) {
      console.error(`Notification Feed Error: ${e}`);
      // TODO: расскомментить, когда починится баг или когда будут докидываться доп инфа
      // logger.error(e, {
      //   sentryTitle: "Header: notification feed error",
      // })
    }
  };

  getUnreadImportantNtfs = (skip = 0) => {
    if (!this.rootStore.feedSettings.ntfIsActivated) return;

    this.NotificationsApi.getUnreadImportantNtfs(this.unreadRequestSize, skip)
      .then(({ data }) => {
        this.setUnreadImportantNtfs(data);
      })
      .catch(() => {
        this.haveUnread = false;
      });
  };

  setUnshownImportantCounter = (count) => {
    if (count === 0) this.haveUnread = false;

    this.unshownImportantCounter = count - this.importantCount;
  };

  setUnreadImportantNtfs = (ntfs) => {
    if (ntfs.length < this.unreadRequestSize) this.haveUnread = false;

    if (!ntfs.length) return;

    sortArrayOfObjectsByDateField(ntfs, "CreatedOn").forEach((ntf) => {
      const notification = new FeedNotification(this.rootStore, ntf);
      this.notificationList.set(notification.id, notification);
    });

    this.setUnshownImportantCounter(
      this.rootStore.notifications.notificationsNumber
    );
  };

  gotChatMessage = (ntf, channelName) => {
    if (this.rootStore.feedSettings.isChatPage) return;

    const { data } = ntf;

    if (!ntf.uid) throw new TypeError("'uid' of undefined");
    if (!data.from && !data.text && !data.user)
      throw new TypeError("Some important Chat data of undefined");

    data.IsChat = true;
    data.channelName = channelName;

    data.groupId = data.id;
    data.id = ntf.uid;
    data.channelId = ntf.channel;

    const notification = new FeedNotification(this.rootStore, data);

    this.notificationList.set(ntf.uid, notification);
  };

  gotNotification = (ntf) => {
    const { entity } = ntf;

    if (
      !entity.id &&
      !entity.default_action_name &&
      !entity.type &&
      !entity.message
    ) {
      throw new TypeError("Some important Ntf data of undefined");
    }

    if (!this.rootStore.feedSettings.ntfIsActivated) return;

    const notification = new FeedNotification(this.rootStore, entity);

    this.notificationList.set(entity.id, notification);
  };

  stopAllExpires = () => {
    this.timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.timeouts = new Map();
  };

  startAllExpires = () => {
    this.notificationList.forEach((ntf) => this.startExpire(ntf));
  };

  /** Начать процесс исчезновения. По окончании таймера нотификация пропадет из списка */
  startExpire = (ntf) => {
    if (ntf.isImportant || this.timeouts.has(ntf.id)) return;

    const timeout = setTimeout(() => {
      this.closeNotification(ntf.id, null, true);
    }, 10000);

    this.timeouts.set(ntf.id, timeout);
  };

  /**
   * Закрыть нотификацию. Работает как для чата, так и для обычных нотификаций
   * Требуемые поля хранятся
   */
  closeNotification = (id, deliveryId, preserveStatusChange) => {
    const currentNotification = this.notificationList.get(id);

    if (!currentNotification) return;
    const timeoutId = this.timeouts.get(id);

    currentNotification.close();
    clearTimeout(timeoutId);
    this.timeouts.delete(id);

    if (
      id &&
      currentNotification &&
      !currentNotification.isChat &&
      !preserveStatusChange
    ) {
      this.sendReadStatus(id, deliveryId);
    }

    setTimeout(() => {
      runInAction(() => {
        this.notificationList.delete(id);
      });
    }, 350);
  };

  /** Скрыть нотификацию вместе с привязанными к ней. Функция используется для showChatMessagesText фичи */
  closeGroupOfChatMessages = (id) => {
    if (!id) return;

    const currentNotification = this.notificationList.get(id);

    if (!currentNotification) return;

    if (currentNotification.groupedChatMessagesCounter > 1) {
      currentNotification.groupedMessages.forEach((messageId) => {
        const groupedNotification = this.notificationList.get(messageId);

        if (!groupedNotification) return;

        const timeoutId = this.timeouts.get(messageId);
        groupedNotification.close();
        clearTimeout(timeoutId);
        this.timeouts.delete(messageId);

        this.notificationList.delete(messageId);
      });
    }

    const timeoutId = this.timeouts.get(id);
    currentNotification.close();
    clearTimeout(timeoutId);
    this.timeouts.delete(id);
    this.notificationList.delete(id);
  };

  closeAllChannelMessages = (channelId) => {
    if (this.notificationList.size > 0) {
      this.notificationList.forEach((value, key) => {
        if (value.isChat && value.channelId === channelId) {
          const timeoutId = this.timeouts.get(key);

          value.close();
          clearTimeout(timeoutId);
          this.timeouts.delete(key);

          runInAction(() => {
            this.notificationList.delete(key);
          });
        }
      });
    }
  };

  sendReadStatus = (id, deliveryId) => {
    this.NotificationsApi.readNtf(id, deliveryId).catch((e) => {
      console.error("Error: can't set the notification as read", e);
    });
  };

  /** Прочесть все сообщения в списке notificationList */
  checkAllMessages = () => {
    this.notificationList.forEach((notification) => {
      this.closeNotification(notification.id, notification.deliveryId);
    });

    callWindowFunction("publishToEventBus", [
      {
        subject: "feedCheckAllMessages",
        destination: "exceptThisTab",
        body: {},
      },
    ]);
  };

  /** Очистка всего списка нотификаций без чтения */
  clearNotificationList = () => {
    this.notificationList = new Map();
  };
}

export default FeedsStore;
